import React from "react";
import Tippy from "@tippyjs/react";
import PropTypes from "prop-types";
import cx from "classnames";
import { Link } from "../../sharedComponents/Link";
import { Section } from "../../sharedComponents/Section/Section";
import { SlideInMenu } from "./SlideInMenu";
import { Icon } from "../Icons";
import { OktaHeader } from "./OktaHeader";
import { ComponentBlock } from "../ComponentBlock";
import { RCL } from "../RCL";
import { AffiliateAlert } from "./AffiliateAlert";
import * as styles from "./Header.module.scss";

const Header = ({
	logo,
	logoLink,
	links,
	ctaMessageLink,
	menuTitle,
	menuLinks,
	locale,
	altLangPages,
	homepagePaths,
	logoOnly,
	widget,
	alerts,
	travelAlertsUrl,
	pageType,
	className,
}) => {
	const lang = locale?.toLowerCase()?.substring(0, 2) === "fr" ? "fr" : "en";

	// TODO: Temporary solution for setting "active" state on nav link based on the booking search box type
	const navLinkSelectedType =
		{
			packages: "5QmzDSJYHQ9gyrYCBbjakL",
			flights: "6KoWZxU0yhufc5hv8EY9RF",
			hotels: "5eCp9pRjhDyQzl2kISajGm",
			cruises: "434UrPZHgLciUEhTwJB8RE",
		}[pageType?.toLowerCase()] || "5QmzDSJYHQ9gyrYCBbjakL";

	const getHomepageUrl = () => {
		const homepageUrl = homepagePaths?.edges?.find(edge => {
			const homepageLocale = edge?.node?.context?.locale?.toUpperCase()?.substring(0, 2);
			return homepageLocale === lang.toUpperCase();
		});

		return homepageUrl?.node?.path ?? "/";
	};

	const homePageUrl = getHomepageUrl();
	const logoLinkPageUrl = getHomepageUrl() + logoLink;

	return (
		<header className={cx("page__header", className)}>
			<AffiliateAlert />
			{logoLink && logoLink !== "homepage" && (
				<Section padding="none" className={styles.headerAlternateLink}>
					<Link to={homePageUrl}>
						<Icon name="arrow-left" /> <RCL searchKey="back-to-home" />
					</Link>
				</Section>
			)}
			<Section padding="none" theme="none" className={styles.header}>
				{ctaMessageLink && (
					<div className={styles.headerCtaMessageWrapper}>
						<Link
							to={ctaMessageLink.url}
							target={ctaMessageLink.target}
							className={styles.headerCtaMessageLink}>
							<Icon name={ctaMessageLink.iconName} />
							{ctaMessageLink.linkText}
						</Link>
					</div>
				)}
				<div className={styles.headerWrapper}>
					<div className={styles.headerImage}>
						{logoLink && (
							<Link to={logoLink !== "homepage" ? logoLinkPageUrl : homePageUrl}>
								{logo && <img src={logo} alt={RCL({ searchKey: "back-to-home" })} />}
							</Link>
						)}
						{!logoLink && logo && <img src={logo} alt={RCL({ searchKey: "logo-sunwing" })} />}
					</div>
					{!logoOnly && (
						<>
							{links && !!links.length && (
								<nav className={styles.headerNav}>
									<ul className={styles.headerNavItems}>
										{links.map(link => (
											<li key={link.linkText}>
												<Link
													to={link.url}
													className={cx(styles.headerNavLink, {
														[styles.headerNavLinkActive]:
															widget &&
															widget.componentType === "search-box" &&
															navLinkSelectedType === link.contentful_id,
													})}>
													{link.linkText}
												</Link>
											</li>
										))}
									</ul>
								</nav>
							)}
							<div className={styles.headerSecondaryNav}>
								<Tippy
									placement="bottom"
									content={
										<div className={styles.tooltip}>
											<RCL searchKey="travel-advisory" />
										</div>
									}>
									<Link
										to={travelAlertsUrl}
										className={`${styles.headerBtn} ${styles.headerBtnInfo}`}>
										{alerts >= 1 && <span className={styles.headerBtnNotification}>{alerts}</span>}
										<span className={styles.headerBtnInfoWrapper}>
											<Icon name="bell" />
										</span>
									</Link>
								</Tippy>
								<OktaHeader locale={locale} />
								{menuLinks && (
									<SlideInMenu
										title={menuTitle}
										menuLinks={menuLinks}
										locale={locale}
										altLangPages={altLangPages}
									/>
								)}
							</div>
						</>
					)}
				</div>
			</Section>
			{widget && (
				<Section padding="none" className={styles.widget}>
					<ComponentBlock data={{ lang, type: pageType?.toLowerCase(), ...widget }} />
				</Section>
			)}
		</header>
	);
};

Header.propTypes = {
	logo: PropTypes.string,
	logoLink: PropTypes.string,
	links: PropTypes.arrayOf(PropTypes.object),
	ctaMessageLink: PropTypes.shape({
		linkText: PropTypes.string,
		url: PropTypes.string,
		target: PropTypes.string,
		iconName: PropTypes.string,
	}),
	menuTitle: PropTypes.string,
	menuLinks: PropTypes.arrayOf(PropTypes.object),
	locale: PropTypes.string.isRequired,
	altLangPages: PropTypes.shape({
		edges: PropTypes.arrayOf(
			PropTypes.shape({
				node: PropTypes.shape({
					context: PropTypes.shape({
						id: PropTypes.string,
						locale: PropTypes.string,
					}),
					path: PropTypes.string,
				}),
			})
		),
	}).isRequired,
	homepagePaths: PropTypes.shape({
		edges: PropTypes.arrayOf(
			PropTypes.shape({
				node: PropTypes.shape({
					context: PropTypes.shape({
						locale: PropTypes.string,
					}),
					path: PropTypes.string,
				}),
			})
		),
	}),
	logoOnly: PropTypes.bool,
	widget: PropTypes.shape({
		title: PropTypes.string,
		componentType: PropTypes.string,
		configuration: PropTypes.string,
	}),
	alerts: PropTypes.number,
	travelAlertsUrl: PropTypes.string.isRequired,
	pageType: PropTypes.oneOf(["Packages", "Flights", "Hotels", "Cruises", "Other"]),
	className: PropTypes.string,
};
Header.defaultProps = {
	logo:
		"https://img.sunwingtravelgroup.com/image/upload/v1614627905/sunwing-prod/Web%20Replatform%20Project/Logos/SunwingLogos/NewSunwingLogo-white.svg",
	logoLink: "",
	links: [],
	ctaMessageLink: undefined,
	menuTitle: null,
	menuLinks: [],
	homepagePaths: null,
	logoOnly: false,
	widget: PropTypes.shape({
		title: "Search Box",
		componentType: "search-box",
		configuration: null,
	}),
	alerts: null,
	pageType: "Packages",
	className: undefined,
};

export default Header;
export { Header };
